import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { WidgetService } from 'src/app/services/widget.service';
import { AssetsService } from 'src/app/services/assets.service';
import { Asset, Artifact } from 'src/app/models/asset';
import { SocketService } from 'src/app/services/socket.service';
import { DialogService } from '../../../services/dialog.service';

@Component({
    selector: 'app-assets-widget',
    templateUrl: './assets-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './assets-widget.component.scss'],
    standalone: false
})
export class AssetsWidgetComponent extends BaseWidgetComponent implements OnInit {
  private dialogs = inject(DialogService);
  @Output() onInspect = new EventEmitter<{ parentId: string; type: string; assetName: string; fileName: string }>();
  @Output() onDefinition = new EventEmitter<{ assetName: string }>();

  // Dataset selection
  public datasets: Asset[] = [];
  public selectedDataset: string = null;

  // Loading states
  public isLoading: boolean = true;
  public isLoadingDetail: boolean = false;
  public isUpdating: boolean = false;

  // Detail view data
  public inUse: boolean = false;
  public warn: boolean = false;
  public mostRecentBuild: any = null;
  public displayedColumns: string[] = ['name', 'owner', 'sample_count', 'bytes', 'training'];
  public prodDS = new MatTableDataSource<Artifact>([]);
  public queueDS = new MatTableDataSource<Artifact>([]);

  constructor(
    private _widgetService: WidgetService,
    private _assetsService: AssetsService,
    private _socketService: SocketService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadDatasets();

    // Subscribe to resize events
    this._subs.add = this._widgetService.resize$.subscribe(() => {
      // Handle resize if needed
    });

    // Subscribe to asset updates via socket
    this._socketService.joinRoom('asset');
    this._subs.add = this._socketService.subscribeToRoomEvents('asset', () => {
      if (this.selectedDataset) {
        this.loadDatasetDetail(this.selectedDataset);
      }
    });
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this._socketService.leaveRoom('asset');
  }

  private loadDatasets(): void {
    this.isLoading = true;
    const sub = this._assetsService.load(this.teamName).subscribe((response: Asset[]) => {
      sub.unsubscribe();

      // Sort by lastModified descending to get most recent first
      // lastModified comes as unix timestamp from the API
      this.datasets = response.sort((a, b) => {
        const aTime = (a as any).lastModified ?? 0;
        const bTime = (b as any).lastModified ?? 0;
        return bTime - aTime;
      });

      this.isLoading = false;

      // Auto-select most recent dataset if we have any
      if (this.datasets.length > 0) {
        this.selectedDataset = this.datasets[0].name;
        this.loadDatasetDetail(this.selectedDataset);
      }
    });
  }

  public onDatasetChange(): void {
    if (this.selectedDataset) {
      this.loadDatasetDetail(this.selectedDataset);
    }
  }

  private loadDatasetDetail(assetName: string): void {
    this.isLoadingDetail = true;
    const sub = this._assetsService.get(this.teamName, assetName).subscribe((response: any) => {
      sub.unsubscribe();
      this.isLoadingDetail = false;

      const assets = response;
      this.mostRecentBuild = assets[0]?.latestBuild;
      this.prodDS.data = assets.filter((x) => !x.path.includes('queue'));
      this.queueDS.data = assets.filter((x) => x.path.includes('queue'));
      this.inUse = assets.filter((x) => x.isTraining === true || x.anyBuildsRunning === true).length > 0;
      this.warn = this.queueDS.data.filter((x) => x.isTraining === true).length > 0;
    });
  }

  public async moveToProd(): Promise<void> {
    if (!await this.dialogs.confirm('Are you sure you want to promote queue files to production?')) return;

    this.isUpdating = true;
    const sub = this._assetsService.move(this.teamName, this.selectedDataset).subscribe((response: any) => {
      sub.unsubscribe();
      this.isUpdating = false;
      this.prodDS.data = response.filter((x) => !x.path.includes('queue'));
      this.queueDS.data = response.filter((x) => x.path.includes('queue'));
    });
  }

  public rowClick(row: Artifact, location: string): void {
    const fileName = location === 'queue' ? `queue/${row.name}` : row.name;
    this.onInspect.emit({
      parentId: this.id,
      type: 'asset',
      assetName: this.selectedDataset,
      fileName
    });
  }

  public refresh(): void {
    if (this.selectedDataset) {
      this.loadDatasetDetail(this.selectedDataset);
    } else {
      this.loadDatasets();
    }
  }
}
